import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../utils/rtk-query';
import { companyServiceRTKQuery } from '../companyService/CompanyServiceRTKQuery';

export interface WexCardDetails {
  _id?: string;
  cardNumber: string;
  status: 'ACTIVE' | 'HOLD' | 'INACTIVE' | 'FRAUD';
  fuelAccountNumber?: string;
  email?: string;
}

export interface SetUserCardArg {
  cardId?: string;
  cardNumber: string;
  email: string;
  signupToken: string;
  companyId: string;
  fuelAccountNumber?: string;
}

export interface DeleteUserCardArg {
  cardId: string;
  email: string;
  companyId: string;
  signupToken: string;
}

export interface PendingUserChange {
  _id?: string;
  email: string;
  signupToken: string;
  fraudInviteStatus: UserCompanyInfo['fraudInviteStatus'];
  phone: string;
  pendingUser: boolean;
}

export interface ManageUsersArg {
  companyId: string;
  body: PendingUserChange[];
  fraudPreventionStatus: 'required' | 'optional' | 'removed';
  enableCardless: boolean;
}

interface WexErrorResponse {
  status?: number;
  data?: unknown;
}

export interface UserCompanyInfo {
  _id?: string;
  email: string;
  fuelAccountNumber: string;
  cards: WexCardDetails[];
  fraudInviteStatus: 'accepted' | 'invited' | 'removed';
  firstName: string;
  lastName: string;
  pendingUser: boolean;
  signupToken: string;
  phone: string;
}

export interface WexAccountDetails {
  numberOfWexCards: number;
  wexCards: WexCardDetails[];
}

const matchesUser = (user: UserCompanyInfo, arg: { email: string; signupToken: string }) =>
  arg.email ? user.email === arg.email : !!arg.signupToken && user.signupToken === arg.signupToken;

export const wexServiceApi = createApi({
  reducerPath: 'wexServiceApi',
  baseQuery: axiosBaseQuery({ baseUrl: config.wexServiceUrl }),
  tagTypes: ['CompanyUsers', 'WexAccountDetails', 'Company'],
  endpoints: (builder) => ({
    getCompanyUsers: builder.query<UserCompanyInfo[], string>({
      query: (companyId) => ({
        url: `company/${companyId}/fraud/users`,
        method: 'GET',
      }),
      providesTags: ['CompanyUsers'],
    }),
    getWexAccountDetails: builder.query<WexAccountDetails, string>({
      query: (companyId) => ({
        url: `/account?company_id=${companyId}`,
        method: 'GET',
      }),
      providesTags: ['WexAccountDetails'],
      transformResponse: (response: WexAccountDetails) => {
        const wexCards = (response.wexCards ?? []).filter((card) => card.status !== 'INACTIVE');
        return {
          ...response,
          wexCards,
          numberOfWexCards: wexCards.length,
        };
      },
    }),
    postChangeToFraudStatus: builder.mutation<void, { companyId: string; required: boolean }>({
      query: ({ companyId, required }) => ({
        url: `/company/${companyId}/fraud/requirement`,
        method: 'POST',
        params: { required },
      }),
    }),
    setUserCardInfo: builder.mutation<WexCardDetails, SetUserCardArg>({
      query: ({ cardId, cardNumber, email, signupToken, companyId, fuelAccountNumber }) => ({
        url: '/user/card',
        method: 'POST',
        data: { cardId, cardNumber, email, signupToken, companyId, fuelAccountNumber },
      }),
      transformErrorResponse: (response: WexErrorResponse) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wexServiceApi.util.updateQueryData('getCompanyUsers', arg.companyId, (draft) => {
            const user = draft.find((u) => matchesUser(u, arg));
            if (user) {
              const newCard: WexCardDetails = {
                _id: arg.cardId,
                cardNumber: arg.cardNumber,
                status: 'ACTIVE',
              };
              if (!user.cards) user.cards = [];
              if (!user.cards.some((c) => c.cardNumber === arg.cardNumber)) {
                user.cards.push(newCard);
              }
            }
          }),
        );

        try {
          const { data: created } = await queryFulfilled;
          if (created?._id) {
            dispatch(
              wexServiceApi.util.updateQueryData('getCompanyUsers', arg.companyId, (draft) => {
                const card = draft
                  .find((u) => matchesUser(u, arg))
                  ?.cards?.find((c) => c.cardNumber === arg.cardNumber);
                if (card) {
                  card._id = created._id;
                  card.status = created.status;
                }
              }),
            );
          }
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteUserCardInfo: builder.mutation<WexCardDetails, DeleteUserCardArg>({
      query: ({ cardId, email, signupToken, companyId }) => ({
        url: `/user/card/${cardId}`,
        method: 'DELETE',
        data: { email, signupToken, companyId },
      }),
      transformErrorResponse: (response: WexErrorResponse) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wexServiceApi.util.updateQueryData('getCompanyUsers', arg.companyId, (draft) => {
            const user = draft.find((u) => matchesUser(u, arg));
            if (user) {
              user.cards = user.cards.filter((c) => c._id !== arg.cardId);
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    manageUsers: builder.mutation<UserCompanyInfo[], ManageUsersArg>({
      query: ({ companyId, body, fraudPreventionStatus, enableCardless }) => ({
        url: `/company/${companyId}/fraud/users`,
        method: 'POST',
        data: body,
        params: {
          fraudPreventionStatus,
          enableCardless,
        },
      }),
      transformErrorResponse: (response: WexErrorResponse) => response.data,
      invalidatesTags: ['CompanyUsers'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(companyServiceRTKQuery.util.invalidateTags(['Company']));
      },
    }),
  }),
});

export const {
  useGetCompanyUsersQuery,
  useGetWexAccountDetailsQuery,
  usePostChangeToFraudStatusMutation,
  useSetUserCardInfoMutation,
  useDeleteUserCardInfoMutation,
  useManageUsersMutation,
} = wexServiceApi;
